/* eslint-disable no-await-in-loop */
import { PDF_BASE64_HARD_LIMIT_BYTES, SCAN_BASE64_MAX_BYTES } from '@shared/utils/helpers/base64Convertion';
import { getPdfWorkerSrc } from '@shared/utils/helpers/pdfWorker';

/**
 * КЛИЕНТСКОЕ СЖАТИЕ PDF.
 *
 * Зачем вообще: файл уходит на бэк base64-строкой внутри JSON, то есть занимает в теле запроса
 * на треть больше собственного размера. Пользователи прикладывают сканы на 2–7 МБ, встречаются
 * и 15 МБ — такой файл не пролезает даже через nginx (`client_max_body_size 10m` на обоих
 * шлюзах). До этого модуля PDF не сжимался вовсе: его либо принимали, либо отклоняли.
 *
 * Как сжимаем: pdf.js рендерит каждую страницу в canvas, canvas отдаёт JPEG, pdf-lib собирает
 * из этих JPEG новый документ. Это РАСТЕРИЗАЦИЯ — она необратимо убивает текстовый слой,
 * поиск и выделение текста, поэтому запускается только как аварийный путь (см. ниже).
 *
 * ВАЖНЫЙ КОНТРПРИМЕР (замер на стенде): векторный PDF с текстовым слоем размером 12 782 байта
 * после растеризации РАЗДУВАЕТСЯ до 64 740 байт (506%) при 2200px/q0.8 и до 23 175 (181%)
 * даже при 1240px/q0.65. Поэтому:
 *   - если исходник уже влезает в бюджет — растеризация не запускается вообще;
 *   - если результат не меньше оригинала — возвращаем оригинал.
 *
 * ВТОРОЙ КОНТРПРИМЕР (баг с боя): проверки «стало ли меньше» НЕ ХВАТАЕТ. Векторный PDF на 3.7 МБ
 * с настоящим текстовым слоем и обилием векторной графики после растеризации дал ~800 КБ на
 * 4 страницы — то есть стал МЕНЬШЕ оригинала, защита `notSmaller` не сработала, и документ ушёл
 * картинками с убитым текстом. Размер не отличает документ с текстовым слоем от скана в принципе:
 * «тяжёлая вектора + текст» ужимается в JPEG так же охотно, как фотография. Поэтому перед
 * растеризацией мы отдельно СПРАШИВАЕМ У ДОКУМЕНТА, есть ли в нём текстовый слой
 * (`page.getTextContent()`), и при его наличии стараемся отдать оригинал как есть.
 *
 * Тяжёлые зависимости (pdfjs-dist ~1.2 МБ + pdf-lib ~0.7 МБ) подключаются ДИНАМИЧЕСКИМ import()
 * внутри функции, чтобы не утяжелять основной бандл: сжатие нужно единицам пользователей и
 * только в момент выбора файла. Сам модуль статических тяжёлых импортов не имеет — его можно
 * безопасно импортировать из shared/utils/files.
 *
 * pdfjs берётся из `react-pdf` (он и так есть в проекте и уже тянет pdfjs-dist 3.11.174):
 * прямой импорт `pdfjs-dist` под Yarn PnP запрещён, пакет не объявлен в зависимостях корня.
 */

/** Колбэк прогресса растеризации: какая страница обрабатывается и сколько их всего. */
// eslint-disable-next-line no-unused-vars
export type PdfCompressionProgress = (page: number, total: number) => void;

/**
 * Типы pdf.js и pdf-lib выводим из самих модулей через `typeof import(...)`.
 * Прямой `import type { PDFDocumentProxy } from 'pdfjs-dist'` невозможен: пакет не объявлен
 * в зависимостях корня и под Yarn PnP не резолвится. Такая запись типов ничего не тянет
 * в бандл — она полностью стирается компилятором.
 */
type PdfJs = typeof import('react-pdf')['pdfjs'];
type PdfDocumentProxy = Awaited<ReturnType<PdfJs['getDocument']>['promise']>;
type PdfPageProxy = Awaited<ReturnType<PdfDocumentProxy['getPage']>>;
type PdfOps = PdfJs['OPS'];
type PdfLibDocument = Awaited<ReturnType<typeof import('pdf-lib')['PDFDocument']['create']>>;

/**
 * Предел ОБЪЁМА РАБОТЫ растеризации: суммарное число пикселей канвы на весь документ.
 *
 * ЗАЧЕМ метрика в пикселях, а не в страницах (как было — `PDF_MAX_PAGES = 40`). Страница
 * странице не равна, и лимит по их количеству отсекал не то. Замер на реальном файле
 * пользователя (47 страниц, каждая — одна JPEG 828×1169, т.е. ~100 dpi): полный проход по
 * всем 47 страницам занимает 5–7 секунд — а лимит в 40 страниц не пускал этот файл в сжатие
 * вообще. При этом 40 страниц настоящего скана 300 dpi (2480×3508) — это в три раза больше
 * пикселей на страницу, минуты работы, и они по старому лимиту проходили. Считать надо
 * фактическую работу: `страниц × сторона² × пропорции страницы`.
 *
 * ОТКУДА ЧИСЛО. Оно зажато между двумя замеренными точками:
 *   - ПРОПУСТИТЬ: файл пользователя после запрета апскейла — 47 × 1172² × 0.707 ≈ 45.6 Мпкс
 *     (замер: 5–7 с на десктопе);
 *   - ОТСЕЧЬ: 40 страниц по 2200 px — 40 × 2200² × 0.707 ≈ 136.8 Мпкс (по прежнему замеру
 *     ~1 с на страницу, то есть около минуты на десктопе и кратно больше на телефоне).
 * 80 Мпкс — практически геометрическое среднее этих точек (78.8), то есть запас примерно
 * 1.75× в обе стороны, и ни одна из границ не стоит впритык к порогу.
 *
 * СКОЛЬКО ЭТО ПО ВРЕМЕНИ. Из того же замера пропускная способность десктопа — около
 * 10 Мпкс/с с учётом постраничных накладных расходов (47 страниц по 1240 px = 51.1 Мпкс
 * за 5.1 с). 80 Мпкс ≈ 8 секунд на десктопе и порядка 30–40 секунд на телефоне за проход.
 * Больше этого блокировать интерфейс нельзя.
 *
 * Превышение — не ошибка сжатия: возвращаем оригинал с причиной `tooHeavy`, а решение
 * «отправлять или отказать» принимает вызывающий код.
 */
const PDF_MAX_RENDER_PIXELS = 80_000_000;

/**
 * Сколько ПЕРВЫХ страниц опрашиваем — и на наличие текстового слоя, и на родное разрешение
 * растрового содержимого (см. probeNativeMaxSide).
 *
 * Почему не весь документ: `getTextContent()` дешевле полного рендера, но не бесплатен —
 * он всё равно разбирает content stream страницы и подгружает шрифты. На 40-страничном скане
 * это лишние секунды перед тем, как начнётся собственно сжатие. Трёх страниц достаточно:
 * документы с текстовым слоем (договоры, справки, выписки, PDF из Word/1С) несут текст с самой
 * первой страницы, а «титул-картинка + текст дальше» встречается редко и три страницы это
 * покрывают. Титульный лист-обложка без текста при этом не заставит нас ошибочно решить,
 * что текста нет во всём документе.
 */
const TEXT_LAYER_PROBE_PAGES = 3;

/**
 * Порог «текстовый слой ЕСТЬ»: суммарное число НЕПРОБЕЛЬНЫХ символов на опрошенных страницах.
 *
 * Почему не «хотя бы один символ»: у сканов сплошь и рядом встречается технический мусор в
 * текстовом слое — колонтитул сканера, номер страницы, штамп «Скан», подпись плоттера. Один-два
 * десятка символов текстовым слоем не являются, и из-за них нельзя отказываться от сжатия
 * многомегабайтного скана.
 * Почему именно 200: страница осмысленного текста A4 — это тысячи знаков (только один абзац
 * даёт 300–500), то есть реальный документ проскакивает порог с запасом в десятки раз, а
 * колонтитул/номер страницы (единицы–десятки знаков) до него не дотягивает даже на трёх
 * страницах. Порог намеренно взят «с большим зазором в обе стороны», чтобы не зависеть от
 * точности подсчёта.
 */
const TEXT_LAYER_MIN_CHARS = 200;

/**
 * Ошибка обработки PDF, которую надо показать пользователю человеческим текстом.
 * Отдельный класс, а не голый Error: нужен i18n-ключ, иначе в тосте появится техтекст
 * pdf.js или пустая «Ошибка».
 *
 * Параметров интерполяции здесь нет намеренно: оба текста (`pdfUnreadable`, `pdfTooHeavy`)
 * самодостаточны. Раньше поле `params` несло `{ limit: PDF_MAX_PAGES }` для сообщения про
 * «не больше 40 страниц» — лимита в страницах больше нет, и подставлять стало нечего.
 */
export class PdfCompressionError extends Error {
  /** i18n-ключ сообщения (namespace common) */
  readonly i18nKey: string;

  constructor(i18nKey: string) {
    super(i18nKey);
    this.name = 'PdfCompressionError';
    this.i18nKey = i18nKey;
  }
}

/** Почему вернули исходный файл вместо результата растеризации. */
export type PdfCompressionSkipReason =
  /** оригинал и так укладывается в бюджет — растеризацию не запускали */
  | 'alreadyFits'
  /** работы больше PDF_MAX_RENDER_PIXELS — растеризацию не запускали */
  | 'tooHeavy'
  /**
   * в документе есть текстовый слой, и он пролезает в границу отказа — растеризацию
   * НЕ запускали намеренно, чтобы не убить текст (см. compressPdfToBudget, ШАГ 4)
   */
  | 'hasTextLayer'
  /** растеризация дала файл не меньше оригинала (тот самый векторный PDF с текстом) */
  | 'notSmaller';

export interface CompressedPdf {
  /** base64 БЕЗ префикса `data:...;base64,` — ровно то, что уходит в теле запроса */
  base64: string;
  /** длина base64 в символах (= размер полезной нагрузки в байтах) */
  size: number;
  /** уложились ли в бюджет */
  withinBudget: boolean;
  /** число страниц; 0 — документ не открывали (оригинал сразу влез в бюджет) */
  pageCount: number;
  /** true — отдали результат растеризации, false — исходный файл как есть */
  rasterized: boolean;
  /** заполнено только при rasterized=false */
  skipReason?: PdfCompressionSkipReason;
  /**
   * ФАКТИЧЕСКАЯ длинная сторона страницы в пикселях, с которой получен результат
   * (для логов/диагностики). Может быть меньше maxSide ступени: сторона ограничена
   * родным разрешением содержимого, см. effectiveMaxSide.
   */
  maxSide?: number;
  quality?: number;
}

interface RasterStep {
  /** целевая ДЛИННАЯ сторона страницы в пикселях */
  maxSide: number;
  /** качество JPEG для canvas.toDataURL (0..1) */
  quality: number;
  /**
   * ОЖИДАЕМЫЙ вес одной страницы в байтах бинарника — из замера на стенде
   * (скан 25.8 МБ / 10 страниц 300 dpi): 2.47 / 1.42 / 0.77 / 0.50 МБ на 10 страниц.
   * Последняя ступень замером не покрыта, значение экстраполировано.
   * Нужен ТОЛЬКО для выбора стартовой ступени, на фактический результат не влияет.
   */
  expectedBytesPerPage: number;
}

/**
 * Лестница растеризации. Качество снижается вместе со стороной: у скана основной вес дают
 * шум и полутона, и уменьшение только разрешения без снижения quality почти не помогает.
 */
const RASTER_STEPS: ReadonlyArray<RasterStep> = [
  { maxSide: 2200, quality: 0.8, expectedBytesPerPage: 247000 },
  { maxSide: 1800, quality: 0.75, expectedBytesPerPage: 142000 },
  { maxSide: 1600, quality: 0.7, expectedBytesPerPage: 77000 },
  { maxSide: 1240, quality: 0.65, expectedBytesPerPage: 50000 },
  { maxSide: 1000, quality: 0.6, expectedBytesPerPage: 33000 }
];

/** base64 длиннее бинарника ровно на треть — коэффициент перевода бюджета в байты файла. */
const BASE64_OVERHEAD = 4 / 3;

/**
 * Перевод Uint8Array в base64 кусками по 32 КБ.
 *
 * Почему не общий getBase64/arrayBufferToBase64 из base64Convertion: первый строит
 * `Array.from(new Uint8Array(...))` — для файла 15 МБ это массив из 15 млн JS-чисел
 * (сотни мегабайт кучи), второй склеивает строку побайтово. Здесь файл заведомо крупный,
 * поэтому идём чанками: и по памяти, и по скорости на порядок дешевле.
 * Чанк 0x8000 выбран потому, что спред в String.fromCharCode ограничен размером стека
 * аргументов (~64k у большинства движков) — 32 768 берём с двукратным запасом.
 */
const bytesToBase64 = (bytes: Uint8Array): string => {
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
};

/**
 * Выбор ступени лестницы: первая, чей ожидаемый вес страницы укладывается в постраничный бюджет.
 *
 * ЗАЧЕМ (умный стартовый шаг): проход по документу стоит ~1 секунду на страницу (замер: первый
 * проход по 10-страничному скану — 11 секунд). Начинать всегда с самой качественной ступени
 * значило бы дарить пользователю заведомо бесполезное ожидание там, где результат очевидно
 * не влезет. Проверка на замере: бюджет 2 МиБ на 10 страниц = 209 715 байт на страницу,
 * ступень 2200/0.8 ожидает 247 000 — не проходит и пропускается (и действительно дала 2.47 МБ),
 * ступень 1800/0.75 ожидает 142 000 — проходит (и действительно дала 1.42 МБ). Для документа
 * на 1–2 страницы бюджет на страницу огромный, и старт остаётся на самой качественной ступени.
 *
 * @param calibration поправка «факт / ожидание», измеренная на уже выполненном проходе.
 *   На первом проходе равна 1. Позволяет после неудачи прыгнуть сразу на нужную ступень,
 *   а не спускаться по одной, тратя по проходу на каждую.
 */
const pickStep = (perPageBudget: number, fromIndex: number, calibration: number): number => {
  for (let i = Math.max(fromIndex, 0); i < RASTER_STEPS.length; i += 1) {
    if (RASTER_STEPS[i].expectedBytesPerPage * calibration <= perPageBudget) return i;
  }
  // Ни одна ступень не обещает попадания в бюджет — берём самую агрессивную и вернём
  // то, что получится: решение об отказе принимает вызывающий код по своей границе.
  return RASTER_STEPS.length - 1;
};

/** Пауза на один тик макрозадачи, чтобы браузер успел перерисовать индикатор прогресса. */
const yieldToBrowser = (): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, 0);
  });

/**
 * Есть ли в документе настоящий текстовый слой.
 *
 * Опрашиваем `getTextContent()` на первых TEXT_LAYER_PROBE_PAGES страницах и считаем суммарную
 * длину НЕПРОБЕЛЬНЫХ символов: пробелы и переводы строк отбрасываем, потому что в текстовом слое
 * сканов их бывает много (позиционирование), а смысла они не несут.
 *
 * Документ сюда приходит УЖЕ ОТКРЫТЫМ (PDFDocumentProxy) и повторно не открывается: открытие
 * 15-мегабайтного PDF стоит дорого, а вызывается эта проверка ровно там, где документ и так
 * распарсен ради растеризации.
 *
 * Ошибку разбора текста намеренно ГЛОТАЕМ и считаем «текста нет»: если pdf.js не смог прочитать
 * content stream страницы, терять всё равно нечего — падать из-за диагностической проверки и
 * отказывать пользователю в загрузке файла нельзя.
 */
const hasTextLayer = async (pdf: PdfDocumentProxy, pageCount: number): Promise<boolean> => {
  const probePages = Math.min(TEXT_LAYER_PROBE_PAGES, pageCount);
  let chars = 0;

  for (let pageNo = 1; pageNo <= probePages; pageNo += 1) {
    try {
      const page = await pdf.getPage(pageNo);
      const content = await page.getTextContent();

      for (let i = 0; i < content.items.length; i += 1) {
        const item = content.items[i];
        // items — объединение TextItem | TextMarkedContent; строка есть только у первого.
        const str = 'str' in item ? item.str : '';
        chars += str.replace(/\s/g, '').length;
      }

      // Разобранный текст держать в памяти незачем — страница может тянуть за собой шрифты.
      page.cleanup();
    } catch {
      // см. комментарий выше: страница не разобралась — просто не учитываем её.
    }

    // Порог набран — дальше листать смысла нет, экономим разбор оставшихся страниц.
    if (chars >= TEXT_LAYER_MIN_CHARS) return true;
  }

  return false;
};

/** Матрица преобразования PDF `[a b c d e f]`. */
type PdfMatrix = [number, number, number, number, number, number];

const IDENTITY_MATRIX: PdfMatrix = [1, 0, 0, 1, 0, 0];

/** Аргумент оператора → матрица, если это действительно шесть конечных чисел. */
const toMatrix = (value: unknown): PdfMatrix | null => {
  if (!Array.isArray(value) || value.length !== 6) return null;
  for (let i = 0; i < 6; i += 1) {
    if (typeof value[i] !== 'number' || !Number.isFinite(value[i])) return null;
  }
  return value as PdfMatrix;
};

/**
 * Конкатенация матриц, как это делает оператор `cm`: сначала применяется m, затем уже
 * накопленная base. Порядок важен — перепутав его, мы получили бы неверный масштаб.
 */
const concatMatrix = (m: PdfMatrix, base: PdfMatrix): PdfMatrix => [
  m[0] * base[0] + m[1] * base[2],
  m[0] * base[1] + m[1] * base[3],
  m[2] * base[0] + m[3] * base[2],
  m[2] * base[1] + m[3] * base[3],
  m[4] * base[0] + m[5] * base[2] + base[4],
  m[4] * base[1] + m[5] * base[3] + base[5]
];

/** Размеры растра из объекта изображения pdf.js, если они там есть и осмысленны. */
const imageSizeOf = (value: unknown): { width: number; height: number } | null => {
  const img = value as { width?: unknown; height?: unknown } | null | undefined;
  if (!img || typeof img.width !== 'number' || typeof img.height !== 'number') return null;
  if (!(img.width > 0) || !(img.height > 0)) return null;
  return { width: img.width, height: img.height };
};

/**
 * Размер растра в пикселях для операторов отрисовки изображения. Не изображение — null.
 *
 * Откуда размеры (pdfjs-dist 3.11.174):
 *   - `paintImageXObject` — аргументы `[objId, width, height]`, где width/height это
 *     `Width`/`Height` из словаря изображения, то есть его РОДНОЕ разрешение. Резервный
 *     путь — спросить сам объект у `page.objs`;
 *   - `paintInlineImageXObject` (инлайновые BI/ID/EI) и `paintImageMaskXObject`
 *     (трафаретная маска `ImageMask true` — типичный чёрно-белый факс-скан) — аргумент
 *     уже готовый объект с полями width/height.
 * Групповые и «повторные» варианты (`*Group`, `*Repeat`) здесь не разбираем сознательно:
 * их собирает QueueOptimizer, а `getOperatorList()` идёт с интентом OPLIST, для которого
 * pdf.js ставит NullOptimizer, — до нас такие операторы не доходят.
 */
const imagePixelSize = (
  ops: PdfOps,
  page: PdfPageProxy,
  fn: number,
  args: unknown
): { width: number; height: number } | null => {
  const list: unknown[] = Array.isArray(args) ? args : [];

  if (fn === ops.paintImageXObject) {
    const [objId, width, height] = list;
    if (typeof width === 'number' && typeof height === 'number' && width > 0 && height > 0) {
      return { width, height };
    }
    if (typeof objId !== 'string') return null;
    try {
      // Резерв на случай, если размеров в аргументах почему-то не оказалось. `objs.get()`
      // БРОСАЕТ, пока данные не доехали из воркера, — ловим и считаем, что ограничения нет:
      // падать из-за диагностической проверки и отказывать в загрузке файла нельзя.
      return imageSizeOf(page.objs.get(objId));
    } catch {
      return null;
    }
  }

  if (fn === ops.paintInlineImageXObject || fn === ops.paintImageMaskXObject) {
    return imageSizeOf(list[0]);
  }

  return null;
};

/**
 * РОДНОЕ разрешение содержимого: какой длинной стороне страницы в пикселях соответствует
 * фактическое разрешение встроенных в неё растров. `null` — растров нет (чистый вектор)
 * или определить не удалось.
 *
 * ЗАЧЕМ. Лестница задаёт целевую сторону, отталкиваясь от размера страницы в ПУНКТАХ
 * (A4 = 595×842), а не от разрешения её содержимого. Скан, у которого страница — это одна
 * JPEG 828×1169, мы рендерили в 1554×2200, то есть растягивали вдвое и жали растянутое.
 * Качества от этого не появляется, а вес растёт: замер на файле пользователя — 16.1 МБ при
 * 2200 px против 7.1 МБ оригинала (227%). Ограничив сторону родным разрешением, мы просто
 * перестаём выдумывать пиксели.
 *
 * КАК СЧИТАЕМ. Мало знать размер картинки в пикселях — важно, какую часть страницы она
 * занимает: мелкий логотип 200×80 на векторной странице не должен ужимать всю страницу до
 * 200 px. Поэтому идём по операторному списку, ведём текущую матрицу преобразования (CTM) и
 * для каждого изображения считаем ПЛОТНОСТЬ — пикселей растра на пункт страницы. Изображение
 * в PDF всегда рисуется в единичный квадрат, растянутый текущей матрицей, поэтому его размер
 * на странице в пунктах — это длины базисных векторов матрицы. Плотность × длинная сторона
 * страницы и есть искомая «родная» сторона. Тот же логотип даёт высокую плотность (200 px на
 * 20 пунктов = 10 px/пункт → 8420 px на страницу), то есть ограничения не накладывает, —
 * ошибка уходит в безопасную сторону.
 *
 * Опрашиваем первые TEXT_LAYER_PROBE_PAGES страниц (ту же константу, что и текстовый слой)
 * и берём МАКСИМУМ: заниженная оценка испортила бы качество, завышенная лишь вернёт прежнее
 * поведение. Ошибки разбора страницы глотаем — так же, как в hasTextLayer.
 */
const probeNativeMaxSide = async (pdf: PdfDocumentProxy, pageCount: number, ops: PdfOps): Promise<number | null> => {
  const probePages = Math.min(TEXT_LAYER_PROBE_PAGES, pageCount);
  let nativeSide = 0;

  for (let pageNo = 1; pageNo <= probePages; pageNo += 1) {
    try {
      const page = await pdf.getPage(pageNo);
      const unscaled = page.getViewport({ scale: 1 });
      const pageSide = Math.max(unscaled.width, unscaled.height);
      const { fnArray, argsArray } = await page.getOperatorList();

      let matrix = IDENTITY_MATRIX;
      const stack: PdfMatrix[] = [];

      for (let i = 0; i < fnArray.length; i += 1) {
        const fn = fnArray[i];
        const args: unknown = argsArray[i];

        if (fn === ops.save) {
          stack.push(matrix);
        } else if (fn === ops.paintFormXObjectBegin) {
          // args = [matrix, bbox]; при отрисовке pdf.js делает здесь неявные save + transform.
          stack.push(matrix);
          const inner = toMatrix(Array.isArray(args) ? args[0] : null);
          if (inner) matrix = concatMatrix(inner, matrix);
        } else if (fn === ops.restore || fn === ops.paintFormXObjectEnd) {
          matrix = stack.pop() ?? IDENTITY_MATRIX;
        } else if (fn === ops.transform) {
          const inner = toMatrix(args);
          if (inner) matrix = concatMatrix(inner, matrix);
        } else {
          const size = imagePixelSize(ops, page, fn, args);

          if (size) {
            const drawnWidth = Math.hypot(matrix[0], matrix[1]);
            const drawnHeight = Math.hypot(matrix[2], matrix[3]);

            // Вырожденную матрицу (картинка схлопнута в точку) пропускаем: деление на неё
            // дало бы астрономическую плотность и обессмыслило максимум.
            if (drawnWidth > 1 && drawnHeight > 1) {
              const density = Math.max(size.width / drawnWidth, size.height / drawnHeight);
              nativeSide = Math.max(nativeSide, Math.ceil(density * pageSide));
            }
          }
        }
      }

      // Операторный список тянет за собой ДЕКОДИРОВАННЫЕ растры (страница 300 dpi — это
      // десятки мегабайт RGBA). Не освободив их сразу, мы вошли бы в растеризацию с уже
      // распухшей вкладкой, а на телефоне это верный способ получить её убийство.
      page.cleanup();
    } catch {
      // Страница не разобралась — просто не учитываем её.
    }
  }

  return nativeSide > 0 ? nativeSide : null;
};

/**
 * Целевая длинная сторона страницы для ступени С УЧЁТОМ родного разрешения содержимого.
 * Родное разрешение неизвестно (чистый вектор или определить не удалось) — ограничения нет:
 * у вектора родного разрешения не существует, и «апскейл» ему не вредит.
 */
const effectiveMaxSide = (step: RasterStep, nativeMaxSide: number | null): number =>
  nativeMaxSide && nativeMaxSide > 0 ? Math.min(step.maxSide, nativeMaxSide) : step.maxSide;

/**
 * Один проход растеризации всего документа с заданными стороной и качеством.
 * Возвращает base64 собранного PDF.
 */
const rasterizePass = async (
  pdf: PdfDocumentProxy,
  createPdfDocument: () => Promise<PdfLibDocument>,
  step: RasterStep,
  nativeMaxSide: number | null,
  pageCount: number,
  onProgress?: PdfCompressionProgress
): Promise<string> => {
  // НЕ АПСКЕЙЛИМ: сторона ступени ограничена родным разрешением содержимого. Считаем её один
  // раз на проход — она одна на весь документ, как и сама ступень.
  const targetSide = effectiveMaxSide(step, nativeMaxSide);
  const out = await createPdfDocument();

  for (let pageNo = 1; pageNo <= pageCount; pageNo += 1) {
    const page = await pdf.getPage(pageNo);

    // Размеры страницы в пунктах PDF (72 dpi) — сохраняем их у выходной страницы,
    // иначе документ поедет в печать в другом физическом формате.
    const unscaled = page.getViewport({ scale: 1 });
    const scale = targetSide / Math.max(unscaled.width, unscaled.height);
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(viewport.width));
    canvas.height = Math.max(1, Math.round(viewport.height));

    const context = canvas.getContext('2d');
    if (!context) throw new PdfCompressionError('common:pdfUnreadable');

    // JPEG не умеет прозрачность: без белой заливки прозрачный фон страницы станет чёрным.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    await page.render({ canvasContext: context, viewport }).promise;
    const dataUrl = canvas.toDataURL('image/jpeg', step.quality);

    // Освобождаем растр СРАЗУ, не дожидаясь сборщика мусора: страница 2200px — это ~25 МБ
    // несжатого битмапа, на 10+ страницах вкладка распухает на сотни мегабайт и мобильный
    // браузер её убивает. Обнуление размеров canvas отдаёт буфер немедленно.
    canvas.width = 0;
    canvas.height = 0;
    page.cleanup();

    const jpg = await out.embedJpg(dataUrl);
    const outPage = out.addPage([unscaled.width, unscaled.height]);
    outPage.drawImage(jpg, { x: 0, y: 0, width: unscaled.width, height: unscaled.height });

    onProgress?.(pageNo, pageCount);
    // Рендер и toDataURL идут в главном потоке — без явной паузы React не успеет
    // перерисовать индикатор, и пользователь увидит просто зависший интерфейс.
    await yieldToBrowser();
  }

  return out.saveAsBase64();
};

/**
 * Сжимает PDF под бюджет (в символах base64 — именно это уходит в теле запроса).
 *
 * @param file исходный PDF
 * @param maxBase64Bytes ЦЕЛЬ в длине base64. По умолчанию SCAN_BASE64_MAX_BYTES — заявленный
 *   `doc_types.max_size` = 2 МиБ файла. Недостижение цели НЕ означает отказ: вызывающий код
 *   сверяется со своей границей отказа (для PDF это PDF_BASE64_HARD_LIMIT_BYTES).
 * @param onProgress колбэк прогресса растеризации
 *
 * ПОРЯДОК ПРИНЯТИЯ РЕШЕНИЯ:
 *   1. влезает в цель                       → оригинал, skipReason='alreadyFits';
 *   2. есть текстовый слой и файл ≤ границы отказа (PDF_BASE64_HARD_LIMIT_BYTES)
 *                                           → оригинал, skipReason='hasTextLayer';
 *   3. работы больше PDF_MAX_RENDER_PIXELS  → оригинал, skipReason='tooHeavy';
 *   4. есть текстовый слой, но файл > границы отказа
 *                                           → растеризуем;
 *   5. текстового слоя нет                  → растеризуем по лестнице;
 *   6. растеризация не дала выигрыша        → оригинал, skipReason='notSmaller'.
 *
 * @throws PdfCompressionError('common:pdfUnreadable') если pdf.js не смог открыть документ
 *   (битый файл, зашифрованный, не PDF под расширением .pdf).
 */
export const compressPdfToBudget = async (
  file: File,
  maxBase64Bytes?: number | null,
  onProgress?: PdfCompressionProgress
): Promise<CompressedPdf> => {
  // Дефолт вынесен в тело, а не в сигнатуру: параметр по умолчанию не может стоять
  // перед необязательным onProgress (eslint default-param-last).
  const budgetBytes = maxBase64Bytes && maxBase64Bytes > 0 ? maxBase64Bytes : SCAN_BASE64_MAX_BYTES;

  const sourceBytes = new Uint8Array(await file.arrayBuffer());
  const sourceBase64 = bytesToBase64(sourceBytes);

  // ШАГ 1. Оригинал уже влезает — не трогаем его совсем. Растеризация здесь только навредила бы:
  // потеряла текстовый слой и (для векторных PDF) увеличила размер.
  if (sourceBase64.length <= budgetBytes) {
    return {
      base64: sourceBase64,
      size: sourceBase64.length,
      withinBudget: true,
      pageCount: 0,
      rasterized: false,
      skipReason: 'alreadyFits'
    };
  }

  // ШАГ 2. Только теперь подтягиваем тяжёлые библиотеки — они не нужны большинству загрузок.
  const [{ pdfjs }, { PDFDocument }] = await Promise.all([import('react-pdf'), import('pdf-lib')]);

  // pdf.js без workerSrc падает синхронно («No GlobalWorkerOptions.workerSrc specified»).
  // Берём локальную копию из public (см. pdfWorker.ts): на воркере держится всё сжатие,
  // и зависеть тут от внешнего CDN нельзя. Ставим, только если он ещё не задан —
  // если просмотрщик уже смонтирован, переопределять его настройку не нужно.
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = getPdfWorkerSrc(pdfjs.version);
  }

  let pdf: PdfDocumentProxy;
  try {
    pdf = await pdfjs.getDocument({ data: sourceBytes }).promise;
  } catch {
    // Битый, зашифрованный или вообще не PDF — молча падать нельзя, иначе пользователь
    // увидит пустой тост «Ошибка» и не поймёт, что делать.
    throw new PdfCompressionError('common:pdfUnreadable');
  }

  const pageCount: number = pdf.numPages;

  // Документ без страниц собрать не из чего, а деление бюджета на 0 дало бы Infinity.
  if (pageCount < 1) {
    await pdf.destroy();
    throw new PdfCompressionError('common:pdfUnreadable');
  }

  // ШАГ 3. Есть ли текстовый слой. Проверка стоит ИМЕННО ЗДЕСЬ:
  //  - после ШАГА 1 — документ, уложившийся в цель, вообще не доходит до этого места, и лишний
  //    разбор текста на нём не тратится;
  //  - ДО проверки бюджета (ШАГ 4) — она единственная умеет закончить работу совсем, а стоит
  //    дешевле: getTextContent разбирает content stream, но не декодирует растры, тогда как
  //    getOperatorList в probeNativeMaxSide декодирует их полностью. Документ с текстовым
  //    слоем в границе отказа выходит отсюда, не заплатив за разбор изображений;
  //  - до ШАГА 6 — иначе мы бы уже испортили документ, ради которого и делается проверка.
  const documentHasText = await hasTextLayer(pdf, pageCount);

  // ГЛАВНЫЙ СЛУЧАЙ РАДИ КОТОРОГО ВСЁ ЗАТЕВАЛОСЬ. Текст в документе есть, и файл при этом
  // пролезает в границу отказа — отдаём ОРИГИНАЛ без растеризации. Документ на 3–7 МБ
  // сервер примет как есть, а вот убитый текстовый слой не вернуть ничем: пользователь
  // теряет поиск, копирование и выделение в договоре/справке. Размен очевидно в пользу
  // текста, поэтому цель сжатия (2 МиБ) здесь сознательно не достигается.
  if (documentHasText && sourceBase64.length <= PDF_BASE64_HARD_LIMIT_BYTES) {
    await pdf.destroy();
    return {
      base64: sourceBase64,
      size: sourceBase64.length,
      withinBudget: false,
      pageCount,
      rasterized: false,
      skipReason: 'hasTextLayer'
    };
  }

  // Если текст есть, но мы прошли дальше — размен обратный: файл не пролезает в границу отказа,
  // то есть без сжатия его ОТКЛОНЯТ И ЗАГРУЗИТЬ НЕ ПОЛУЧИТСЯ ВООБЩЕ. Документ с картинками
  // лучше, чем отсутствие документа, поэтому растеризуем несмотря на текстовый слой — и
  const nativeMaxSide = await probeNativeMaxSide(pdf, pageCount, pdfjs.OPS);

  // ШАГ 5. Бюджет работы. Пропорции берём у первой страницы: канва не квадратная, у A4
  // короткая сторона составляет 0.707 длинной, и без этой поправки оценка была бы завышена
  // почти в полтора раза.
  let pageAspect = 1;
  try {
    const firstViewport = (await pdf.getPage(1)).getViewport({ scale: 1 });
    const pageLongSide = Math.max(firstViewport.width, firstViewport.height);
    if (pageLongSide > 0) pageAspect = Math.min(firstViewport.width, firstViewport.height) / pageLongSide;
  } catch {
    // Страница не открылась — считаем её квадратной. Это ЗАВЫШАЕТ оценку работы, то есть
    // ошибка уходит в сторону осторожности; настоящую причину («файл не читается») выдаст
    // растеризация ниже, у неё для этого есть свой обработчик.
  }

  // Оцениваем САМЫЙ дорогой возможный проход — по верхней ступени лестницы, уже ограниченной
  // родным разрешением. По стартовой ступени оценивать нельзя: у длинного документа
  // постраничный бюджет мал, pickStep сразу уводит на 1000 px, и 40-страничный скан 300 dpi
  // выглядел бы дешёвым — хотя декодирование его исходных растров (2480×3508 на страницу)
  // никуда не девается и стоит тех же минут.
  const plannedSide = effectiveMaxSide(RASTER_STEPS[0], nativeMaxSide);
  const plannedPixels = pageCount * plannedSide * plannedSide * pageAspect;

  if (plannedPixels > PDF_MAX_RENDER_PIXELS) {
    await pdf.destroy();
    return {
      base64: sourceBase64,
      size: sourceBase64.length,
      withinBudget: false,
      pageCount,
      rasterized: false,
      skipReason: 'tooHeavy'
    };
  }

  // ШАГ 6. Лестница. Бюджет переводим из длины base64 в байты бинарника и делим на страницы:
  // ступени описаны в байтах на страницу.
  const perPageBudget = Math.floor(budgetBytes / BASE64_OVERHEAD / pageCount);
  let stepIndex = pickStep(perPageBudget, 0, 1);
  let best: { base64: string; step: RasterStep } | null = null;

  // pdf.js продвигает page.render() через requestAnimationFrame, а Chrome НЕ вызывает rAF в
  // фоновой вкладке. Пользователь бросил файл и ушёл читать почту в соседней вкладке — сжатие
  // молча замирает на первой странице и оживает только когда он вернётся: ни ошибки, ни
  // прогресса, «ничего не происходит». Проверено на тесте: скан 4.8 МБ в фоне висит больше
  // 45 секунд, он же в активной вкладке сжимается за 2.5 секунды.
  //
  // На время растеризации подменяем rAF своим «тиком»: кадровая синхронизация рендеру тут не
  // нужна, важно лишь, чтобы продолжение вызвалось. Тик делаем на MessageChannel, а НЕ на
  // setTimeout: фоновым вкладкам Chrome режет таймеры примерно до одного срабатывания в
  // секунду, и на setTimeout сжатие хоть и доходило до конца, но вчетверо медленнее
  // (замер: 3 страницы в фоне — 29.8 с против 2.6 с на страницу в активной вкладке).
  // Сообщения MessageChannel этому троттлингу не подвержены.
  //
  // В finally возвращаем оригиналы: иначе сломали бы анимации остального приложения.
  // cancelAnimationFrame подменяем тоже — pdf.js отменяет запрошенные кадры, и без парной
  // подмены он передавал бы наш id в родной cancel, отменяя чужие кадры приложения.
  const originalRaf = window.requestAnimationFrame;
  const originalCancelRaf = window.cancelAnimationFrame;
  const tickCallbacks = new Map<number, FrameRequestCallback>();
  const tickChannel = new MessageChannel();
  let nextTickId = 1;

  tickChannel.port1.onmessage = (event: MessageEvent<number>) => {
    const cb = tickCallbacks.get(event.data);
    if (!cb) return;
    tickCallbacks.delete(event.data);
    cb(performance.now());
  };

  window.requestAnimationFrame = (cb: FrameRequestCallback): number => {
    const id = nextTickId;
    nextTickId += 1;
    tickCallbacks.set(id, cb);
    tickChannel.port2.postMessage(id);
    return id;
  };
  window.cancelAnimationFrame = (id: number): void => {
    tickCallbacks.delete(id);
  };

  try {
    while (stepIndex < RASTER_STEPS.length) {
      const step = RASTER_STEPS[stepIndex];
      const base64 = await rasterizePass(pdf, () => PDFDocument.create(), step, nativeMaxSide, pageCount, onProgress);

      if (!best || base64.length < best.base64.length) best = { base64, step };
      if (base64.length <= budgetBytes) break;

      // Не влезли. Пересчитываем ожидания по факту этого прохода и прыгаем сразу на ту
      // ступень, которая при такой же «плотности» документа должна пройти, — иначе на
      // тяжёлом скане мы бы потратили по проходу (по секунде на страницу) на каждую ступень.
      const actualBytesPerPage = base64.length / BASE64_OVERHEAD / pageCount;
      const calibration = actualBytesPerPage / step.expectedBytesPerPage;
      const nextIndex = pickStep(perPageBudget, stepIndex + 1, calibration);
      if (nextIndex <= stepIndex) break;
      stepIndex = nextIndex;
    }
  } catch (e) {
    // Рендер отдельной страницы тоже может упасть (повреждённое содержимое, неподдержанный
    // шрифт). Показываем ту же понятную причину, а не техтекст pdf.js в тосте.
    if (e instanceof PdfCompressionError) throw e;
    throw new PdfCompressionError('common:pdfUnreadable');
  } finally {
    window.requestAnimationFrame = originalRaf;
    window.cancelAnimationFrame = originalCancelRaf;
    // Порты держат ссылку на обработчик и не собираются сборщиком сами — закрываем явно,
    // иначе каждая загрузка оставляла бы после себя живой канал.
    tickCallbacks.clear();
    tickChannel.port1.close();
    tickChannel.port2.close();
    await pdf.destroy();
  }

  const result = best;
  // ШАГ 7. Растеризация не дала выигрыша — это ровно случай векторного PDF с текстовым слоем
  // (замер: 12 782 → 64 740 байт). Отдаём оригинал: он и меньше, и с сохранённым текстом.
  // Проверку НЕ убираем и после появления ШАГА 4: она страхует случай, когда текстового слоя
  // нет (или он не набрал порог), а растеризация всё равно раздувает файл.
  if (!result || result.base64.length >= sourceBase64.length) {
    return {
      base64: sourceBase64,
      size: sourceBase64.length,
      withinBudget: false,
      pageCount,
      rasterized: false,
      skipReason: 'notSmaller'
    };
  }

  return {
    base64: result.base64,
    size: result.base64.length,
    withinBudget: result.base64.length <= budgetBytes,
    pageCount,
    rasterized: true,
    // Текст был и его растеризовали — вызывающий код обязан предупредить пользователя.
    // Именно ФАКТИЧЕСКАЯ сторона, а не сторона ступени: при ограничении родным разрешением
    // они расходятся, и в лог должно попасть то, что реально отрисовано.
    maxSide: effectiveMaxSide(result.step, nativeMaxSide),
    quality: result.step.quality
  };
};
